"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PreSaleCard = PreSaleCard;
var jsx_runtime_1 = require("react/jsx-runtime");
// ================================================================
// v2/components/PreSaleCard/index.tsx
// 自分のチケット用カード (Figma: TypeNewDesign2)。
// GlassCard (glass=true → background.sub 55% + CardShadow, cornerRadius
// radius.sm = 8, border なし, 左に primary の accentBar) を外殻に使い、
//   - 任意 MembershipBadge
//   - タイトル (14px W600 -0.05px foreground.neutral.primary / JP/Subtitle1)
//   - 任意 skuTitle (13px W300)
//   - ステータス行 (Badge 群 + 任意 resaleLabel 右寄せ foreground.semantic.info)
//   - 任意 保有枚数 (ラベル + 枚数)
//   - 任意 リセール価格 (ラベル + 金額。どちらも表示文言を props で受ける)
//   - 任意 cautionText (background.warning.subtle / foreground.semantic.warning)
//   - 任意 応募期間セル (background.neutral.subtle)
//   - 任意ボタン群 (ピル: primary / outline)
// を縦に積む。色はすべて theme.palette.v2 (useV2Palette) から取得する。
//
// 表示文言はこのパッケージが i18n を持たないため、すべて props で受ける
// (通貨の桁区切りも locale 依存なので、整形済みの文字列を渡してもらう)。
// ================================================================
var material_1 = require("@mui/material");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
var Badge_1 = require("../Badge");
var GlassCard_1 = require("../GlassCard");
var MembershipBadge_1 = require("../MembershipBadge");
// ── Internal ─────────────────────────────────────────────────────
/**
 * 「ラベル 値」を1行で出す JP/Caption (12px W4)。
 * ラベルは secondary、値は primary。値は locale 依存の整形を含むため
 * 呼び出し側で整形済みの文字列を受け取る。
 */
function LabeledCaption(_a) {
    var label = _a.label, value = _a.value;
    var p = (0, theme_1.useV2Palette)();
    return ((0, jsx_runtime_1.jsxs)(material_1.Typography, { sx: {
            fontSize: 12,
            fontWeight: tokens_1.fontWeight.normal,
            lineHeight: '16px',
        }, children: [(0, jsx_runtime_1.jsx)(material_1.Box, { component: "span", sx: { color: p.foreground.neutral.secondary }, children: label }), (0, jsx_runtime_1.jsxs)(material_1.Box, { component: "span", sx: { color: p.foreground.neutral.primary }, children: [' ', value] })] }));
}
// ── Component ────────────────────────────────────────────────────
function PreSaleCard(_a) {
    var memberBadgeLabel = _a.memberBadgeLabel, title = _a.title, skuTitle = _a.skuTitle, _b = _a.statusBadges, statusBadges = _b === void 0 ? [] : _b, ownedCountLabel = _a.ownedCountLabel, ownedCountText = _a.ownedCountText, resaleLabel = _a.resaleLabel, resalePriceLabel = _a.resalePriceLabel, resalePriceText = _a.resalePriceText, cautionText = _a.cautionText, applicationPeriod = _a.applicationPeriod, _c = _a.actions, actions = _c === void 0 ? [] : _c;
    var p = (0, theme_1.useV2Palette)();
    return ((0, jsx_runtime_1.jsxs)(GlassCard_1.GlassCard, { cornerRadius: tokens_1.radius.sm, bordered: false, accentBar: true, children: [(0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: "".concat(tokens_1.spacing.sm, "px"), sx: {
                    p: "".concat(tokens_1.spacing.md, "px"),
                }, children: [memberBadgeLabel && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: { alignSelf: 'flex-start' }, children: (0, jsx_runtime_1.jsx)(MembershipBadge_1.MembershipBadge, { label: memberBadgeLabel }) })), (0, jsx_runtime_1.jsxs)(material_1.Stack, { spacing: "".concat(tokens_1.spacing.sm, "px"), sx: { width: '100%' }, children: [(0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                                    width: '100%',
                                    fontSize: 14,
                                    fontWeight: tokens_1.fontWeight.semiBold,
                                    lineHeight: '20px',
                                    letterSpacing: '-0.05px',
                                    color: p.foreground.neutral.primary,
                                }, children: title }), skuTitle && ((0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                                    width: '100%',
                                    fontSize: 13,
                                    fontWeight: 300,
                                    lineHeight: '20px',
                                    color: p.foreground.neutral.primary,
                                }, children: skuTitle })), (statusBadges.length > 0 || !!resaleLabel) && ((0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "row", alignItems: "center", spacing: "".concat(tokens_1.spacing.xs, "px"), sx: { minHeight: 20, width: '100%' }, children: [statusBadges.map(function (badge, i) { return ((0, jsx_runtime_1.jsx)(Badge_1.Badge, { label: badge.label, variant: badge.variant, color: badge.color, endIcon: badge.endIcon }, i)); }), resaleLabel && ((0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                                            flex: 1,
                                            textAlign: 'right',
                                            fontSize: 13,
                                            fontWeight: tokens_1.fontWeight.semiBold,
                                            color: p.foreground.semantic.info,
                                            whiteSpace: 'nowrap',
                                        }, children: resaleLabel }))] })), ownedCountLabel && ownedCountText && ((0, jsx_runtime_1.jsx)(LabeledCaption, { label: ownedCountLabel, value: ownedCountText }))] }), resalePriceLabel && resalePriceText && ((0, jsx_runtime_1.jsx)(LabeledCaption, { label: resalePriceLabel, value: resalePriceText })), cautionText && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                            backgroundColor: p.background.warning.subtle,
                            borderRadius: "".concat(tokens_1.radius.sm, "px"),
                            px: "".concat(tokens_1.spacing.md - 4, "px"),
                            py: "".concat(tokens_1.spacing.sm + 2, "px"),
                        }, children: (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                                fontSize: 12,
                                fontWeight: 300,
                                lineHeight: '16px',
                                color: p.foreground.semantic.warning,
                            }, children: cautionText }) }))] }), applicationPeriod && ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
                    minHeight: 28,
                    backgroundColor: p.background.neutral.subtle,
                    px: "".concat(tokens_1.spacing.md, "px"),
                    py: "".concat(tokens_1.spacing.xs, "px"),
                    display: 'flex',
                    alignItems: 'center',
                }, children: (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                        fontSize: 10,
                        fontWeight: 300,
                        lineHeight: '12px',
                        color: p.foreground.neutral.primary,
                    }, children: applicationPeriod }) })), actions.length > 0 && ((0, jsx_runtime_1.jsx)(material_1.Stack, { direction: "row", spacing: "".concat(tokens_1.spacing.sm, "px"), sx: {
                    minHeight: 60,
                    px: "".concat(tokens_1.spacing.md, "px"),
                    alignItems: 'center',
                    justifyContent: 'center',
                }, children: actions.map(function (action, i) {
                    var isPrimary = action.variant === 'primary';
                    return ((0, jsx_runtime_1.jsx)(material_1.Button, { onClick: action.onClick, variant: "text", disableElevation: true, sx: {
                            flex: 1,
                            maxWidth: 240,
                            height: 44,
                            fontSize: 14,
                            fontWeight: tokens_1.fontWeight.semiBold,
                            lineHeight: '20px',
                            letterSpacing: '-0.05px',
                            borderRadius: "".concat(tokens_1.radius.full, "px"), // ピル型
                            boxShadow: 'none',
                            textTransform: 'none',
                            // NOTE: テーマの MuiButton.containedPrimary が primary.gradient を
                            // `background`(=background-image) で塗るため、backgroundColor だけでは
                            // グラデが上に残る。`background` ショートハンドで塗り替えて単色にする。
                            background: isPrimary
                                ? p.background.primary.default
                                : p.background.neutral.default,
                            // primary の面はアプリのブランドカラー (withV2BrandColors) に
                            // 差し替わるため、文字色は面の明度から決める
                            color: isPrimary
                                ? (0, theme_1.pickFilledInk)(p.background.primary.default, p)
                                : p.foreground.neutral.primary,
                            '&:hover': {
                                boxShadow: 'none',
                                background: isPrimary
                                    ? p.foreground.primary.main
                                    : p.background.neutral.default,
                                filter: 'brightness(0.95)',
                            },
                        }, children: action.label }, i));
                }) }))] }));
}
